import {
  B2cCartPayload,
  B2cCartProduct,
  CartProductValidationMessage,
  CartTotal,
  CartUpdateProductDocument,
  CartSimpleDocument,
} from "@/generated/graphql.lazy/b2c";
import { CartDocument } from "@/generated/graphql.lazy/b2c";
import { savingTypes } from "@/constants/cart-errors";

import { Api } from "@/api";
import { Context } from "@/services/context";

export const CART_LISTENER_NAME = "cart";

type UpdateProductParams = {
  productId: string;
  cartProductId?: string;
  quantity: number;
  priceId: string;
  wholeCartValidation?: boolean;
};

export class Cart {
  id: string | undefined;
  items: B2cCartProduct[] = [];
  totals: CartTotal[] = [];
  errors: CartProductValidationMessage[] = [];
  savedErrors: CartProductValidationMessage[] = [];
  count = 0;
  loading = false;

  constructor(
    protected context: Context,
    protected api: Api,
  ) { }

  protected update = () => {
    this.context.notify(CART_LISTENER_NAME);
  };

  setPreloadedCart(cart: any | null) {
    if (cart) {
      this.id = cart.id;
      this.items = cart.cartProducts;
      this.totals = cart.totals;
      this.count = this.items.reduce((total, item) => total + item.quantity, 0);

      this.update();
    }
  }

  clear() {
    this.id = undefined;
    this.items = [];
    this.totals = [];
    this.errors = [];
    this.savedErrors = [];
    this.count = 0;

    this.update();
  }

  get subtotal() {
    return this.totals.find((total) => total.type === "sub")?.value;
  }

  get taxTotal() {
    return this.totals.find((total) => total.type === "tax")?.value;
  }

  get taxAdditionalInfo() {
    return this.totals.find((total) => total.type === "tax")?.additionalInfo;
  }

  get discountTotal() {
    return this.totals.find((total) => total.type === "discount")?.value;
  }

  get grandTotal() {
    return this.totals.find((total) => total.type === "grand")?.value;
  }

  get totalWeight() {
    return this.items?.
      filter((item) => !item.isDisabled).
      reduce((previousValue, currentValue) => Number(previousValue) + currentValue.product.weight.value * currentValue.quantity, 0).toFixed(2);
  }

  get errorsList() {
    let errors: CartProductValidationMessage[] = [];
    if (this.errors) {
      errors = errors.concat(this.errors);
    }

    if (this.savedErrors) {
      errors = errors.concat(this.savedErrors);
    }

    this.items.forEach((item) => {
      if (item.errors) {
        errors = errors.concat(item.errors);
      }
    });

    return errors;
  }

  get hasErrors() {
    return this.errors?.length > 0 || this.savedErrors?.length > 0 || this.items.some((item) => item.errors?.length > 0);
  }

  fetchSimpleCart = async () => {
    try {
      const payload = await this.requestSimpleCart();

      if (!payload) {
        return null;
      }

      if (!this.isFullCartLoadStarted()) {
        this.setPreloadedCart(payload);
      }

    } catch (error: any) {
      console.error("Fetching cart failed:", error);
      throw new Error(error.message);
    }
  };

  fetchCart = async (clearErrors = true) => {
    try {
      this.loading = true;

      const payload = await this.requestCurrentCart();

      if (!payload) {
        return null;
      }

      if (clearErrors) {
        this.savedErrors = [];
      } else {
        payload.cartProducts.forEach((product) => {
          product.errors ??= [];
          product.errors.push(...this.savedErrors.filter((error) => error.productId === product.id));
        });
      }

      this.updateValueByCart(payload);
    } catch (error: any) {
      console.error("Fetching cart failed:", error);
      throw new Error(error.message);
    } finally {
      this.loading = false;
      this.update();
    }
  };

  updateItem = async (params: UpdateProductParams) => {
    const payload = await this.mutateUpdateItem(params);

    this.updateValueByCart(payload);
    this.update();
  };

  getItem = (productId: string, priceId: string | null) => {
    let found;
    if (priceId) {
      found = this.items.find((item) => item.product.id === productId && item.priceId === priceId);
    } else {
      found = this.items.find((item) => item.product.id === productId);
    }

    if (found) {
      return {
        added: true as const,
        cartProductId: found.id,
        quantity: found.quantity,
        priceId: found.priceId,
      };
    }

    return { added: false as const };
  };

  protected updateValueByCart = (cart: B2cCartPayload) => {
    this.id = cart.id;
    this.totals = cart.totals;
    this.items = cart.cartProducts;
    this.errors = cart.errors ?? [];
    this.count = this.items.reduce((total, item) => total + item.quantity, 0);

    const productsErrors = cart.cartProducts.reduce(
      (errors, product) => [...errors, ...product.errors ?? []], [] as CartProductValidationMessage[]
    );

    this.savedErrors = [
      ...this.savedErrors,
      ...this.errors.filter((error) => savingTypes.includes(error.type)),
      ...productsErrors.filter((error) => savingTypes.includes(error.type)),
    ];
  };

  protected mutateUpdateItem = async (params: UpdateProductParams) => {
    const input = structuredClone(params) as Partial<UpdateProductParams>;
    if (input.cartProductId) {
      // cartProductId is priority field
      delete input.productId;
    }

    const cartResponse = await this.api.client.mutate({
      mutation: CartUpdateProductDocument,
      variables: {
        input: {
          wholeCartValidation: input.wholeCartValidation,
          cartProductId: input.cartProductId,
          productId: input.productId,
          quantity: input.quantity!,
          priceId: input.priceId!,
          cartId: this.id,
        },
      },
    });

    return cartResponse.data!.CartUpdateProduct as B2cCartPayload;
  };

  protected requestSimpleCart = async () => {
    const cartResponse = await this.api.client.query({
      query: CartSimpleDocument,
      variables: {
        recalculateCart: false,
      },
    });

    return cartResponse.data.Cart as B2cCartPayload;
  };

  protected requestCurrentCart = async () => {
    const cartResponse = await this.api.client.query({
      query: CartDocument,
    });

    return cartResponse.data.Cart as B2cCartPayload;
  };

  protected isFullCartLoadStarted = () => {
    return this.loading || this.id;
  }
}

export type InCartState = {
  added: true;
  cartProductId: string;
  quantity: number;
  priceId: string;
} | {
  added: false;
  cartProductId?: undefined;
  quantity?: undefined;
  priceId?: undefined;
};